<script lang="ts">
  import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
  import { IOrderItem } from '../../../interfaces/IOrderItem';
  import DisplayValue from '../../DisplayValue.vue';
  import { Globals } from '../../../modules/Globals';
  import { getDisplayOptions, roundPrice } from '../../../utils';
  import { ICurrency } from '../../../interfaces/ICurrency';
  import AddItemsModal from '../new-order/AddItemsModal.vue';
  import { vueData } from '../../../VueData';
  import IModifyOrderDto from '../../../interfaces/dtos/orders/IModifyOrderDto';
  import OrdersService from '../../../api/services/orders.service';
  import { IOrder } from '../../../interfaces/IOrder';
  import { OrderStatus } from '../../../enums/OrderEnums';
  import { AlertType } from '@/interfaces/IAlert';
  import NewOrderModal from '../NewOrderModal.vue';
  @Component({
    components: { DisplayValue },
  })
  export default class OrderItems extends Vue {
    @Prop() currency: ICurrency;
    @Prop() order: IOrder;
    @Prop() items: IOrderItem[];
    editingItems: IOrderItem[] = null;
    uploadsPath = Globals.uploadsPath;
    saving = false;

    mounted() {}

    get orderItems() {
      return this.editingItems || this.items;
    }

    get optionsByIndex() {
      return getDisplayOptions(this.orderItems);
    }

    toggleEditing() {
      /*this.editingItems = this.editingItems
        ? null
        : JSON.parse(JSON.stringify(this.items));*/
      this.$modal.show(
        NewOrderModal,
        {
          restaurant: vueData.restaurant,
          branch: vueData.branch,
          currency: vueData.currency,
          restaurantSettings: vueData.restaurantSettings,
          editingOrder: this.order,
          editingOrderItems: this.items,
        },
        {
          name: 'new-order',
          classes: 'dialog--height-limit',
          adaptive: true,
          height: '95%',
          clickToClose: false,
          width: '100%',
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
          transition: 'none',
        },
        {
          update: () => {
            this.$emit('update');
          },
        }
      );
    }

    removeItem(item: IOrderItem) {
      if (!item['_new']) {
        item['_changed'] = true;
        Vue.set(item, 'canceledAt', new Date());
      } else {
        const index = this.editingItems.indexOf(item);
        if (index > -1) this.editingItems.splice(index, 1);
      }
    }

    async openAddItemDialog() {
      this.$modal.show(
        AddItemsModal,
        {
          restaurant: vueData.restaurant,
          branch: vueData.branch,
          currency: vueData.currency,
          restaurantSettings: vueData.restaurantSettings,
          menuId: this.items[0].item.menuId,
          totals: {
            totalPrice: 0,
            totalCount: 0,
            taxAmount: 0,
            subTotal: 0,
          },
        },
        {
          name: 'add-items',
          adaptive: true,
          classes: 'dialog--height-limit',
          height: window.innerHeight * 0.95,
          width: 960,
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
        },
        {
          add: (orderItem: IOrderItem) => {
            orderItem.manual = true;
            orderItem['_new'] = true;

            // If an item with the same id and options is already in the list, just increase the amount
            const existing = this.editingItems.find(
              (i) =>
                i['_new'] &&
                i.itemId === orderItem.itemId &&
                !orderItem.options.length &&
                !i.options.length
            );

            if (existing) {
              const unitPrice = existing.total / existing.amount;
              existing.amount += orderItem.amount;
              existing.total = roundPrice(unitPrice * existing.amount, true);
            } else {
              this.editingItems.push(orderItem);
            }

            vueData.addAlert(
              `${orderItem.item.name} agregado`,
              AlertType.Success,
              1000
            );
          },
        }
      );
    }

    async save() {
      if (!this.editingItems) return;

      const dto: IModifyOrderDto = {
        add: [],
        cancelIds: [],
      };

      for (const item of this.editingItems) {
        if (item['_new']) {
          const i = { ...item };
          // @ts-ignore
          i.options = i.options.map((option) => ({
            id: option.optionItemId,
            val: option.val,
          }));
          dto.add.push({
            itemId: i.itemId,
            amount: i.amount,
            options: i.options,
            comment: i.comment,
          });
        } else if (item['_changed'] && item.canceledAt) {
          dto.cancelIds.push(item.index);
        }
      }

      if (!dto.add.length && !dto.cancelIds.length) {
        this.editingItems = null;
        return;
      }

      try {
        this.saving = true;
        console.log(dto);
        await OrdersService.modify(this.order.id, dto);
        this.editingItems = null;
        this.$emit('update');
        if (Globals.IsProduction) {
          this.$gtag.event('modify', {
            event_category: 'order',
            event_label: this.order.restaurantId + '',
          });
        }
      } catch (e) {
        vueData.serverError(e);
        console.error(e);
      } finally {
        this.saving = false;
      }
    }
  }
</script>

<template>
  <div class="order-items" :class="{ editing: !!editingItems }">
    <v-row
      align="center"
      justify="end"
      class="mt-n6 mb-2 pr-1"
      v-if="order.status === 'progress'"
    >
      <template v-if="!editingItems">
        <v-btn color="info" dark @click="toggleEditing" small rounded depressed
          ><v-icon small class="mr-1">mdi-pencil</v-icon> Editar</v-btn
        >
      </template>
      <template v-else>
        <v-btn
          color="grey"
          dark
          @click="toggleEditing"
          small
          rounded
          :disabled="saving"
          depressed
          ><v-icon small class="mr-1">mdi-close</v-icon> Cancelar</v-btn
        >
        <v-btn
          color="primary"
          dark
          class="ml-2"
          small
          rounded
          @click="save"
          :loading="saving"
          ><v-icon small class="mr-1">mdi-content-save</v-icon> Guardar</v-btn
        >
      </template>
    </v-row>
    <v-row
      align="center"
      class="order-item flex-nowrap"
      :class="{
        canceled: orderItem.canceledAt,
        manual: orderItem.manual,
        'is-new': orderItem['_new'],
      }"
      v-for="(orderItem, index) of orderItems"
      :key="index"
    >
      <v-chip
        v-if="orderItem.printC === orderItem.amount"
        v-tooltip="'Ya impreso en comanda'"
        class="printed-indicator"
        x-small
        color="info"
      >
        <v-icon x-small>mdi-check</v-icon>
        <v-icon x-small>mdi-printer</v-icon>
      </v-chip>
      <v-col cols="auto">
        <span class="amount">{{ orderItem.amount }}x</span>
      </v-col>
      <v-col cols="auto" class="px-0" v-if="orderItem.item.picture">
        <v-img
          class="picture"
          :src="`${uploadsPath}/${orderItem.item.picture}`"
        ></v-img>
      </v-col>
      <v-col class="meta flex-fill align-self-start">
        <h4 class="name">{{ orderItem.item.name }}</h4>

        <div class="options" v-if="optionsByIndex[orderItem.index]">
          <div
            v-for="(dO, index) of optionsByIndex[orderItem.index]"
            :key="index"
          >
            <template
              v-if="dO.option.type === 'choose' && dO.optionItems.length === 1"
            >
              <display-value
                class="my-0 options__item"
                :title="
                  dO.option.title + ': ' + dO.optionItems[0].optionItem.title
                "
                :client-bold="false"
                :space-between="true"
              >
                <span class="font-weight-medium"
                  >{{ dO.optionItems[0].total > 0 ? currency.symbol : ''
                  }}{{
                    dO.optionItems[0].total > 0 ? dO.optionItems[0].total : '-'
                  }}</span
                >
              </display-value>
            </template>
            <template v-else>
              <h4>{{ dO.option.title }}</h4>
              <display-value
                class="my-0 options__item"
                v-for="option of dO.optionItems"
                :key="option.optionItemId"
                :title="
                  ' - ' +
                  (option.optionItem.option.type === 'add' || option.val > 1
                    ? `${option.val}x `
                    : '') +
                  option.optionItem.title
                "
                :client-bold="false"
                :space-between="true"
              >
                <span class="font-weight-medium"
                  >{{ option.total > 0 ? currency.symbol : ''
                  }}{{ option.total > 0 ? option.total : '-' }}</span
                >
              </display-value>
            </template>
          </div>
        </div>

        <div>
          <span class="float-left"
            >{{ currency.symbol }}{{ orderItem.price }}</span
          >
          <span class="float-right font-weight-bold"
            >{{ currency.symbol }}{{ orderItem.total }}</span
          >
        </div>
        <div class="clear"></div>
        <display-value
          class="comments"
          :title="$t('order.comments')"
          v-if="orderItem.comment"
        >
          <p>{{ orderItem.comment }}</p>
        </display-value>
      </v-col>
      <v-col cols="auto" class="actions" v-if="editingItems">
        <v-btn
          icon
          small
          @click="removeItem(orderItem)"
          :disabled="!!orderItem.canceledAt"
        >
          <v-icon small>mdi-delete</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-row v-if="editingItems" align="center" justify="center" class="mt-2">
      <v-btn
        color="success"
        dark
        small
        rounded
        depressed
        @click="openAddItemDialog"
      >
        <v-icon>mdi-plus</v-icon>
      </v-btn>
    </v-row>
  </div>
</template>

<style scoped lang="scss">
  .order-items {
    padding-bottom: 64px;
  }

  .order-item {
    @apply tw-relative;

    &.manual,
    &.is-new {
      background: repeating-linear-gradient(
        45deg,
        #ffffff,
        #ffffff 8px,
        #e8ffd1 8px,
        #e8ffd1 10px
      );
    }
    &.canceled {
      opacity: 0.4;
      background: repeating-linear-gradient(
        45deg,
        #fafafa,
        #fafafa 8px,
        #ffd6d6 8px,
        #ffd6d6 10px
      );
      text-decoration: line-through !important;
      .float-left,
      .float-right {
        text-decoration: line-through !important;
      }
    }

    .printed-indicator {
      @apply tw-absolute tw-top-2 tw-right-2 tw-flex tw-items-center tw-space-x-2 tw-px-1;
    }
  }

  .editing {
    .manual:not(.is-new):not(.canceled) {
      background: none;
    }
  }
</style>
